import { useMemo, useState } from 'react';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import {
  useAiKnowledgeBaseEntries,
  useCreateAiKnowledgeBaseImport,
  useCreateAiKnowledgeBaseEntry,
  useDeleteAiKnowledgeBaseEntry,
  useUpdateAiKnowledgeBaseEntry,
} from '@/features/company/model/useAiKnowledgeBase';
import type {
  AiKnowledgeBaseEntryDto,
  AiKnowledgeBaseImportJobDto,
} from '@/shared/api/ai-knowledge-base';
import { Notification } from '@/shared/ui';

import { KnowledgeBaseArticleEditor } from './KnowledgeBaseArticleEditor';
import {
  KnowledgeBaseArticleList,
  type KnowledgeBaseFilters,
} from './KnowledgeBaseArticleList';
import { KnowledgeBaseImportPanel } from './KnowledgeBaseImportPanel';
import { KnowledgeBaseUsagePanel } from './KnowledgeBaseUsagePanel';

const defaultFilters: KnowledgeBaseFilters = {
  query: '',
  tag: '',
  enabled: '',
  freshness: '',
};

type SelectionMode = 'auto' | 'selected' | 'create' | 'none';

export function KnowledgeBaseTab({
  companyId,
  disabled,
}: {
  companyId: string;
  disabled: boolean;
}) {
  const { t } = useTranslation('company');
  const [filters, setFilters] = useState(defaultFilters);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectionMode, setSelectionMode] = useState<SelectionMode>('auto');
  const [pendingEntry, setPendingEntry] =
    useState<AiKnowledgeBaseEntryDto | null>(null);
  const [lastImportJob, setLastImportJob] =
    useState<AiKnowledgeBaseImportJobDto | null>(null);
  const [importResetToken, setImportResetToken] = useState(0);
  const [deletedIds, setDeletedIds] = useState<Set<string>>(() => new Set());
  const entriesQuery = useAiKnowledgeBaseEntries(companyId, {
    query: filters.query || undefined,
    tag: filters.tag || undefined,
    enabled: filters.enabled === '' ? undefined : filters.enabled === 'true',
    freshness: filters.freshness || undefined,
    limit: 100,
  });
  const createMutation = useCreateAiKnowledgeBaseEntry(companyId);
  const importMutation = useCreateAiKnowledgeBaseImport(companyId);
  const updateMutation = useUpdateAiKnowledgeBaseEntry(companyId);
  const deleteMutation = useDeleteAiKnowledgeBaseEntry(companyId);
  const entries = useMemo(() => {
    const visible = (entriesQuery.data?.entries ?? []).filter(
      (entry) => !deletedIds.has(entry.id)
    );

    if (!pendingEntry || deletedIds.has(pendingEntry.id)) return visible;
    if (visible.some((entry) => entry.id === pendingEntry.id)) return visible;

    return [pendingEntry, ...visible];
  }, [deletedIds, entriesQuery.data?.entries, pendingEntry]);
  const selectedEntry = useMemo(() => {
    if (selectionMode === 'create' || selectionMode === 'none') return null;
    if (selectionMode === 'selected') {
      return entries.find((entry) => entry.id === selectedId) ?? null;
    }

    return entries[0] ?? null;
  }, [entries, selectedId, selectionMode]);
  const editorBusy =
    createMutation.isPending ||
    importMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;

  return (
    <Stack data-testid="knowledge-base-tab" spacing={2}>
      <Box>
        <Typography variant="h6" fontWeight={800}>
          {t('modules.knowledgeBase.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('modules.knowledgeBase.caption')}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '360px minmax(0, 1fr)' },
          gap: 2,
          alignItems: 'flex-start',
        }}
      >
        <Box
          sx={{
            p: 1.5,
            border: '1px solid',
            borderColor: (theme) => alpha(theme.palette.text.primary, 0.1),
            borderRadius: 1,
            bgcolor: 'background.paper',
          }}
        >
          <KnowledgeBaseArticleList
            entries={entries}
            total={entriesQuery.data?.total ?? entries.length}
            activeCount={entriesQuery.data?.active_count ?? 0}
            selectedId={selectedEntry?.id ?? null}
            filters={filters}
            isLoading={entriesQuery.isPending}
            disabled={disabled || editorBusy}
            onFiltersChange={(nextFilters) => {
              setFilters(nextFilters);
              setPendingEntry(null);
              setSelectedId(null);
              setSelectionMode('auto');
            }}
            onSelect={(id) => {
              setSelectedId(id);
              setSelectionMode('selected');
            }}
            onCreate={() => {
              setPendingEntry(null);
              setSelectedId(null);
              setSelectionMode('create');
            }}
          />
        </Box>

        <Stack
          spacing={2}
          divider={<Divider />}
          sx={{
            p: 1.5,
            border: '1px solid',
            borderColor: (theme) => alpha(theme.palette.text.primary, 0.1),
            borderRadius: 1,
            bgcolor: 'background.paper',
            minWidth: 0,
          }}
        >
          <KnowledgeBaseArticleEditor
            entry={selectedEntry}
            disabled={disabled || editorBusy}
            isSaving={createMutation.isPending || updateMutation.isPending}
            isDeleting={deleteMutation.isPending}
            onCreate={(data) => {
              createMutation.mutate(data, {
                onSuccess: (entry) => {
                  setPendingEntry(entry);
                  setSelectedId(entry.id);
                  setSelectionMode('selected');
                },
              });
            }}
            onSave={(id, data) =>
              updateMutation.mutate({ id, data }, {
                onSuccess: (entry) => {
                  setPendingEntry(entry);
                  setSelectedId(entry.id);
                  setSelectionMode('selected');
                },
              })}
            onDelete={(id) => {
              deleteMutation.mutate(id, {
                onSuccess: () => {
                  setDeletedIds((current) => new Set(current).add(id));
                  setPendingEntry((current) => current?.id === id ? null : current);

                  const nextEntry = entries.find((entry) => entry.id !== id);
                  setSelectedId(nextEntry?.id ?? null);
                  setSelectionMode(nextEntry ? 'selected' : 'none');
                },
              });
            }}
          />
          <KnowledgeBaseUsagePanel
            companyId={companyId}
            entryId={selectedEntry?.id ?? null}
          />
          <KnowledgeBaseImportPanel
            disabled={disabled || importMutation.isPending}
            isImporting={importMutation.isPending}
            lastJob={lastImportJob}
            resetToken={importResetToken}
            onImport={(data) => {
              importMutation.mutate(data, {
                onSuccess: (job) => {
                  setLastImportJob(job);
                  setImportResetToken((value) => value + 1);
                },
              });
            }}
          />
        </Stack>
      </Box>

      <Notification
        open={
          entriesQuery.isError ||
          createMutation.isError ||
          importMutation.isError ||
          updateMutation.isError ||
          deleteMutation.isError
        }
        title={
          entriesQuery.error?.message ||
          createMutation.error?.message ||
          importMutation.error?.message ||
          updateMutation.error?.message ||
          deleteMutation.error?.message
        }
      />
    </Stack>
  );
}
